import { API, Auth } from 'aws-amplify';
import dayjs from '../dayjs';
import { s3Upload } from '../libs/awsLib';
import AdmZip from 'adm-zip';
import config from '../config';
import { bugReportModelData } from '../services/ModelService';

// Helper function to safely stringify objects with circular references
export const safeStringify = (obj) => {
  const seen = new WeakSet();
  return JSON.stringify(obj, (key, value) => {
    // Handle circular references
    if (typeof value === 'object' && value !== null) {
      if (seen.has(value)) {
        return '[Circular Reference]';
      }
      seen.add(value);
    }
    return value;
  });
};

// Helper function to get system information
export const getSystemInfo = () => {
  const info = {
    os: navigator.platform,
    browser: navigator.userAgent,
    screenResolution: `${window.screen.width}x${window.screen.height}`,
    colorDepth: window.screen.colorDepth,
    language: navigator.language,
    memory: navigator.deviceMemory ? `${navigator.deviceMemory}GB` : 'Unknown',
    connection: navigator.connection ? {
      type: navigator.connection.effectiveType,
      downlink: `${navigator.connection.downlink}Mbps`,
      rtt: `${navigator.connection.rtt}ms`
    } : 'Unknown',
    timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    cookiesEnabled: navigator.cookieEnabled,
    doNotTrack: navigator.doNotTrack,
    touchPoints: navigator.maxTouchPoints || 0
  };

  // Add memory usage information if available
  if (window.performance && window.performance.memory) {
    // Chrome-specific memory info
    info.memoryUsage = {
      totalJSHeapSize: formatBytes(window.performance.memory.totalJSHeapSize),
      usedJSHeapSize: formatBytes(window.performance.memory.usedJSHeapSize),
      jsHeapSizeLimit: formatBytes(window.performance.memory.jsHeapSizeLimit)
    };
  } else if (window.performance && window.performance.getEntriesByType) {
    // Try to get memory info from performance entries
    try {
      const memoryEntries = window.performance.getEntriesByType('memory');
      if (memoryEntries && memoryEntries.length > 0) {
        info.memoryUsage = {
          totalJSHeapSize: formatBytes(memoryEntries[0].totalJSHeapSize),
          usedJSHeapSize: formatBytes(memoryEntries[0].usedJSHeapSize),
          jsHeapSizeLimit: formatBytes(memoryEntries[0].jsHeapSizeLimit)
        };
      }
    } catch (e) {
      console.error('Error getting memory info:', e);
    }
  }

  return info;
};

// Helper function to format bytes to human-readable format
export const formatBytes = (bytes) => {
  if (bytes === 0) return '0 Bytes';

  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));

  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};

// Helper to serialize breadcrumbs (string or React element)
export const serializeBreadcrumb = (bc) => {
  if (typeof bc === 'string') return bc;
  if (bc && bc.props && bc.props.children) {
    if (Array.isArray(bc.props.children)) {
      return bc.props.children.map(serializeBreadcrumb).join('');
    }
    return serializeBreadcrumb(bc.props.children);
  }
  return '';
};

// Helper function to upload logs to S3
export const uploadLogsToS3 = async (consoleLogs, operator) => {
  if (consoleLogs.length === 0) return null;

  try {
    // Format logs as a readable text file
    const logContent = consoleLogs.map(log =>
      `[${log.timestamp}] [${log.type.toUpperCase()}] ${log.message}`
    ).join('\n');

    // Create a file with the logs
    const fileName = `bug-report-logs-${dayjs().format('YYYY-MM-DD-HH-mm-ss')}.txt`;

    // Create a proper S3 key path
    const s3Key = `${operator?.operatorId || 'public'}/bug-reports/${dayjs().format('YYYY-MM-DD')}/${fileName}`;

    // Create a zip file using adm-zip
    const zip = new AdmZip();
    zip.addFile(fileName, Buffer.from(logContent));

    // Get the zip buffer
    const zipBuffer = zip.toBuffer();

    // Create a File object from the zip buffer
    const file = new File([zipBuffer], `${fileName}.zip`, { type: 'application/zip' });

    // Upload to S3 using the s3Upload function with proper parameters
    const fileKey = await s3Upload(file, s3Key, {
      level: 'public',
      contentType: 'application/zip'
    });

    // Check if fileKey already contains a full URL
    let s3Url;
    if (fileKey && fileKey.startsWith('http')) {
      // fileKey is already a full URL
      s3Url = fileKey;
    } else {
      // Construct the full S3 URL
      s3Url = `https://${config.s3.BUCKET}.s3.${config.s3.REGION}.amazonaws.com/public/${fileKey}`;
    }
    
    return s3Url;
  } catch (error) {
    console.error('Error uploading logs to S3:', error);
    return null;
  }
};

// Helper function to save bug report to Refs table
export const saveBugReport = async (bugData) => {
  try {
    console.log('Saving bug report to Refs table:', bugData);
    console.log('Reporter data:', bugData.reporter);
    console.log('Reporter name:', bugData.reporter?.name);
    console.log('Reporter email:', bugData.reporter?.email);
    console.log('Reporter id:', bugData.reporter?.id);
    
    // Validate required fields
    if (!bugData.bugId) {
      throw new Error('bugId is required for bug report');
    }
    
    if (!bugData.reporter || !bugData.reporter.name && !bugData.reporter.email) {
      throw new Error('Reporter information is required');
    }
    
    // Create bug report in comment-like structure (same as OTR comments)
    // Use the same pattern as OTR comments: author should be the logged-in user directly
    const author = bugData.author || bugData.reporter?.name || bugData.reporter?.email || 'Unknown User';
    console.log('Setting author to:', author);
    console.log('Author source:');
    console.log('- bugData.author:', bugData.author);
    console.log('- bugData.reporter.name:', bugData.reporter?.name);
    console.log('- bugData.reporter.email:', bugData.reporter?.email);
    
    const bugReport = {
      title: bugData.title || 'Support Ticket',  // Root title field
      description: bugData.description || 'Support Ticket',  // Root description field
      author: author,
      readBy: [author],
      type: 'support_ticket',
      id: bugData.bugId,     // Use id as the primary identifier
      subType: 'general',
      createdAt: Date.now(),
      updatedAt: Date.now(),
      companyId: bugData.companyId,  // Move to root level for API filtering
      operatorId: bugData.operatorId,  // Move to root level for consistency
      // Group remaining ticket-specific data into metaInfo map
      metaInfo: {
        jiraKey: bugData.jiraKey,
        status: bugData.status || 'new',
        priority: bugData.priority || 'medium',
        category: bugData.category || 'general',
        reporter: {
          id: bugData.reporter.id,
          name: bugData.reporter.name,
          email: bugData.reporter.email
        },
        attachments: bugData.attachments || [],
        systemInfo: bugData.systemInfo || {},
        breadcrumbs: bugData.breadcrumbs || '',
        url: bugData.url || ''
      }
    };

    console.log('About to save bug report:', bugReport);
    console.log('Bug report id:', bugReport.id);
    console.log('Bug report type:', bugReport.type);
    
    // Use the same pattern as OTR comments - direct save without fallback
    await bugReportModelData.save(bugReport);
    console.log('Successfully saved bug report to Refs table');
    
    return bugReport;
  } catch (error) {
    console.error('Error saving bug report:', error);
    throw error;
  }
};

// Helper function to fetch company's bug reports
export const fetchCompanyBugReports = async (companyId) => {
  try {
    console.log('Fetching bug reports for company:', companyId);
    
    if (!companyId) {
      console.log('No company ID provided');
      return [];
    }

    // Use the specific fetchByCompanyId method instead of getAll()
    console.log('Using fetchByCompanyId method...');
    const companyReports = await bugReportModelData.fetchByCompanyId(companyId);
    console.log('Bug reports from fetchByCompanyId:', companyReports);
    
    // Filter out archived reports
    const activeReports = companyReports.filter(report => !report.archived);
    
    // Sort by creation date, most recent first
    const sortedReports = activeReports.sort((a, b) => (b.createdAt || 0) - (a.createdAt || 0));
    
    console.log('Filtered and sorted bug reports:', sortedReports);
    return sortedReports;
  } catch (error) {
    console.error('Failed to fetch company bug reports:', error);
    return [];
  }
};

// Bug reports now follow the exact same pattern as OTR comments
// No localStorage fallback needed - uses the same proven infrastructure

// Helper function to fetch JIRA ticket details
export const fetchJiraTicketDetails = async (jiraKey) => {
  try {
    if (!jiraKey) return null;
    
    console.log('🔍 Fetching JIRA ticket details for:', jiraKey);
    
    const response = await API.post('third_party', '/jira/get-issue', {
      body: { issueKeys: [jiraKey] }
    });
    
    if (response.issues && response.issues.length > 0) {
      const issue = response.issues[0];
      console.log('✅ JIRA ticket details fetched:', issue);
      return {
        summary: issue.summary,
        status: issue.status,
        priority: issue.priority,
        updated: issue.updated
      };
    }
    
    return null;
  } catch (error) {
    console.error('Error fetching JIRA ticket details:', error);
    return null;
  }
};

// Helper function to get issue type based on user role
export const getIssueType = (isAdmin, isLocalhost) => {
  if (isAdmin || isLocalhost) {
    return 'Bug';
  }
  return 'Customer Issue';
};

// Helper function to get status color
export const getStatusColor = (status) => {
  switch (status?.toLowerCase()) {
    case 'done':
    case 'resolved':
    case 'closed':
      return '#52c41a';
    case 'in progress':
    case 'in review':
      return '#1890ff';
    case 'to do':
    case 'open':
      return '#faad14';
    case 'blocked':
      return '#ff4d4f';
    default:
      return '#d9d9d9';
  }
};

// Helper function to get deployment status tooltip
export const getDeploymentStatusTooltip = (status) => {
  switch (status) {
    case 'staging':
      return 'This issue was reported from the staging environment';
    case 'pre-prod':
      return 'This issue was reported from the pre-production environment';
    case 'prod':
      return 'This issue was reported from the production environment';
    default:
      return 'Deployment status information';
  }
};
